import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Field, Input, SectionLabel, Select, Textarea } from '@wundo/ui';
import { CohortMultiSearchSelect } from '@/components/pickers/CohortPicker.tsx';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import { listAllSkills } from '@/lib/skillOptions.ts';
import type { CreateSkillBroadcastRequest } from '@/lib/skillBroadcasts.ts';
import { DeliveryRules } from './DeliveryRules.tsx';

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const LOCAL_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/* Today where the person planning is, not where the server is. */
function localToday(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

interface FormState {
  skillId: string;
  cohorts: CohortWithOrganisation[];
  scheduledFor: string;
  sendAtTime: string;
  reason: string;
}

/* "(optional)" belongs in the label, quieter than the name it qualifies. */
function optional(label: string) {
  return (
    <>
      {label} <span className='font-normal text-ink-muted'>(optional)</span>
    </>
  );
}

const EMPTY: FormState = {
  skillId: '',
  cohorts: [],
  scheduledFor: '',
  sendAtTime: '',
  reason: '',
};

export interface SkillBroadcastFormProps {
  saving: boolean;
  /** Shown above the button when the last attempt was refused; empty otherwise. */
  error: string;
  onSubmit: (request: CreateSkillBroadcastRequest) => void;
}

/**
 * Plan a skill push: which skill, to whom, and when.
 *
 * A time is optional, and leaving it out is the meaningful choice rather than
 * the lazy one — without it every user gets the skill at their own notification
 * hour, which is when they are used to hearing from Wundo. The page remounts
 * this on a successful plan, which is what empties the fields.
 */
export function SkillBroadcastForm({ saving, error, onSubmit }: SkillBroadcastFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY);

  const { data: skills } = useQuery({
    queryKey: ['skills', 'all'],
    queryFn: listAllSkills,
  });

  const skillOptions = useMemo(
    () => [
      { value: '', label: 'Choose a skill...' },
      ...(skills ?? []).map((skill) => ({ value: skill.id, label: skill.name })),
    ],
    [skills],
  );

  const canSubmit =
    form.skillId !== '' &&
    form.cohorts.length > 0 &&
    form.scheduledFor >= localToday() &&
    (form.sendAtTime === '' || TIME_PATTERN.test(form.sendAtTime));

  function set(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function submit() {
    if (!canSubmit || saving) return;

    onSubmit({
      skill_id: form.skillId,
      cohort_ids: form.cohorts.map((cohort) => cohort.id),
      scheduled_for: form.scheduledFor,
      send_at: form.sendAtTime
        ? new Date(`${form.scheduledFor}T${form.sendAtTime}`).toISOString()
        : undefined,
      reason: form.reason.trim() || undefined,
    });
  }

  return (
    <Card>
      <div className='flex flex-col gap-5'>
        <SectionLabel as='h2'>Plan a broadcast</SectionLabel>

        <Field label='Skill' htmlFor='skill' required>
          <Select
            id='skill'
            wrapperClassName='w-full'
            className='w-full'
            options={skillOptions}
            value={form.skillId}
            onChange={(event) => set({ skillId: event.target.value })}
          />
        </Field>

        <Field label='Cohorts' htmlFor='cohorts' required>
          <CohortMultiSearchSelect
            id='cohorts'
            value={form.cohorts}
            onChange={(cohorts) => set({ cohorts })}
            placeholder='Select cohorts...'
          />
        </Field>

        <div className='grid grid-cols-2 gap-4 max-mobile:grid-cols-1'>
          <Field label='Scheduled for' htmlFor='scheduled_for' required>
            <Input
              id='scheduled_for'
              type='date'
              value={form.scheduledFor}
              onChange={(event) => set({ scheduledFor: event.target.value })}
            />
          </Field>
          <Field label={optional('Send at')} htmlFor='send_at_time'>
            <Input
              id='send_at_time'
              type='time'
              value={form.sendAtTime}
              onChange={(event) => set({ sendAtTime: event.target.value })}
            />
          </Field>
        </div>

        <p className='-mt-3 text-xs text-ink-muted'>
          {form.sendAtTime
            ? `Fires at this exact moment in your local time (${LOCAL_ZONE}) — one instant for everyone. Picking today sends it right away if the time has already passed.`
            : 'Without a time, each user receives it at their own notification hour on the chosen day.'}
        </p>

        <Field label={optional('Reason')} htmlFor='reason'>
          <Textarea
            id='reason'
            rows={3}
            sizing='manual'
            value={form.reason}
            onChange={(event) => set({ reason: event.target.value })}
            placeholder='Optional: a timely hook for why now. Leave blank to let the skill speak for itself.'
          />
        </Field>

        <DeliveryRules hasSendTime={form.sendAtTime !== ''} />

        {error !== '' && (
          <p role='alert' className='text-sm font-semibold text-danger'>
            {error}
          </p>
        )}

        <div className='flex justify-end border-t border-line pt-4'>
          <Button disabled={!canSubmit} loading={saving} onClick={submit}>
            Plan broadcast
          </Button>
        </div>
      </div>
    </Card>
  );
}
